// Streak math — pure functions, no React, no localStorage.
//
// Rules
// -----
// • A check-in is identified by a YYYY-MM-DD string in the user's LOCAL
//   timezone (idempotent — clicking the button twice in one day is a
//   no-op). UTC would shift days for users west of GMT.
// • Streak counts the number of consecutive days the user actually
//   showed up. Forgiven (grace-skipped) days do NOT count toward the
//   number — the displayed streak is "real days you saved", not
//   "days the streak survived". Grace just keeps the run alive.
// • Up to 1 grace skip per rolling 7-day window. Two grace skips
//   ≥ 7 days apart are both allowed; 6 days apart is not.
// • If the user hasn't checked in TODAY yet, that's not a miss — the
//   streak walks back from yesterday. Once today's local day ends
//   without a check-in, it becomes a real miss the next day.

// ── Date string helpers (LOCAL time, no UTC drift) ────────────────────────

export function todayLocalDateString(date = new Date()) {
  return formatLocalDate(date);
}

export function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseLocalDate(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(s, n) {
  const dt = parseLocalDate(s);
  dt.setDate(dt.getDate() + n);
  return formatLocalDate(dt);
}

function daysBetween(a, b) {
  const da = parseLocalDate(a);
  const db = parseLocalDate(b);
  return Math.abs(Math.round((da - db) / 86_400_000));
}

// ── Streak computation ────────────────────────────────────────────────────

export function computeStreak(checkInDates, today = todayLocalDateString()) {
  const sortedUnique = [...new Set(checkInDates)].sort();
  const checkInSet = new Set(sortedUnique);

  if (sortedUnique.length === 0) {
    return {
      length: 0,
      todayLogged: false,
      graceSkipsUsed: [],
      graceRemainingThisWeek: 1,
    };
  }

  const todayLogged = checkInSet.has(today);
  // If today isn't logged yet, walking from yesterday lets the user keep
  // their streak alive until end-of-day. Clicking the button later today
  // will increment to length+1.
  let cursor = todayLogged ? today : addDays(today, -1);

  let length = 0;
  const graceSkipsUsed = [];
  const oldestCheckIn = sortedUnique[0];

  // Walk backwards. Stop when we go past the first ever check-in (no
  // point gracing dates from before the user even started).
  while (cursor >= oldestCheckIn) {
    if (checkInSet.has(cursor)) {
      length++;
      cursor = addDays(cursor, -1);
      continue;
    }
    // Missed day → attempt grace. Rule violation: any prior grace
    // within 6 days of cursor (i.e., < 7-day separation).
    const wouldViolate = graceSkipsUsed.some(d => daysBetween(d, cursor) <= 6);
    if (wouldViolate) break;
    graceSkipsUsed.push(cursor);
    cursor = addDays(cursor, -1);
  }

  // For the UI: how many grace skips has the user already burned in
  // the trailing 7-day window from today?
  const recentGraceCount = graceSkipsUsed.filter(d => daysBetween(today, d) <= 6).length;
  const graceRemainingThisWeek = Math.max(0, 1 - recentGraceCount);

  return {
    length,
    todayLogged,
    graceSkipsUsed,
    graceRemainingThisWeek,
  };
}

// Build a last-N-days status array for the visual streak strip in the UI.
// Returns oldest → newest with one entry per day:
//   { date, status: 'logged' | 'grace' | 'missed' | 'future' }
export function recentDaysStatus(checkInDates, n = 14, today = todayLocalDateString()) {
  const { graceSkipsUsed } = computeStreak(checkInDates, today);
  const checkInSet = new Set(checkInDates);
  const graceSet = new Set(graceSkipsUsed);
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const date = addDays(today, -i);
    let status;
    if (checkInSet.has(date)) status = 'logged';
    else if (graceSet.has(date)) status = 'grace';
    else status = 'missed';
    out.push({ date, status });
  }
  return out;
}
